import {
  Button,
  CheckboxField,
  ChevronDownIcon,
  Flex,
  Link,
  Loading,
  Popover,
  SearchField,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import type { Column, RowData } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { ActiveFilterTag } from '../activeFilterTag/activeFilterTag';
import {
  StyledFilterList,
  StyledFilterListButton,
  StyledFilterListCheckboxField,
  StyledFilterListItem,
} from '../filter.styles';
import type { ProductSelectFeatures } from './productSelectFeatures';

/**
 * Lives inside the popover's content, so the query resets each time the
 * popover closes
 */
const FilterOptionList = ({
  children,
  filterLabel,
  optionLabel,
  options,
  searchable,
}: {
  children: (visibleOptions: string[]) => ReactNode;
  filterLabel: string;
  optionLabel: (option: string) => string;
  options: string[];
  searchable: boolean;
}) => {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();

  const visibleOptions = normalizedQuery
    ? options.filter((option) =>
        optionLabel(option).toLowerCase().includes(normalizedQuery)
      )
    : options;

  return (
    <>
      {searchable && (
        <Flex px={16} pt={16}>
          <SearchField
            label={`Search ${filterLabel}`}
            labelHidden
            name={`${filterLabel}-filter-search`}
            onChange={setQuery}
            placeholder={`Search ${filterLabel.toLowerCase()}`}
            value={query}
            width="100%"
          />
        </Flex>
      )}
      {children(visibleOptions)}
    </>
  );
};

export const ProductSelectFilter = <R extends RowData>({
  column,
  filterOptions,
  isLoading,
}: {
  column: Column<ProductSelectFeatures, R>;
  filterOptions?: string[];
  isLoading?: boolean;
}) => {
  const activeFilters = (column.getFilterValue() ?? []) as string[];
  const filterLabel =
    column.columnDef.meta?.filterLabel ?? sentenceCase(column.id);

  /**
   * Columns whose options aren't plain API values supply their own labels;
   * the default just turns underscores into spaces.
   */
  const optionLabel =
    column.columnDef.meta?.filterOptionLabel ??
    ((option: string) => sentenceCase(option.replace(/_/g, ' ')));
  const noFilterOptions = !filterOptions?.length && !isLoading;

  const onFilterChange = (option: string) => {
    let newFilterValues: string[];

    if (activeFilters.includes(option)) {
      newFilterValues = activeFilters.filter((filter) => filter !== option);
    } else {
      newFilterValues = [...activeFilters, option];
    }
    column.setFilterValue(newFilterValues);
  };

  if (!filterOptions) {
    return null;
  }

  return (
    <Popover
      label={`Filters for ${filterLabel}`}
      target={
        <Button
          label={filterLabel}
          variant={activeFilters.length ? 'tertiary-selected' : 'tertiary'}
          endIcon={
            <Flex alignItems="center">
              <ActiveFilterTag
                valuesCount={activeFilters.length}
                label={filterLabel}
              />
              <ChevronDownIcon color="inherit" size="sm" />
            </Flex>
          }
          disabled={noFilterOptions}
        />
      }
      openOn={noFilterOptions ? 'disabled' : 'hover'}
      disableMobileStyling
    >
      {({ closePopover }) => (
        <Popover.Content padding={false}>
          {!filterOptions.length && isLoading ? (
            <Flex
              position="relative"
              height="100%"
              p={24}
              aria-label={`Loading ${filterLabel} filter options`}
            >
              <Loading color="purple" />
            </Flex>
          ) : (
            <FilterOptionList
              filterLabel={filterLabel}
              options={filterOptions}
              optionLabel={optionLabel}
              searchable={!!column.columnDef.meta?.filterSearchable}
            >
              {(visibleOptions) => (
                <StyledFilterList aria-label={`${filterLabel} filter options`}>
                  {visibleOptions.map((option) => (
                    <StyledFilterListItem key={option}>
                      <StyledFilterListCheckboxField>
                        <CheckboxField
                          label={optionLabel(option)}
                          name={`${option}-checkbox`}
                          onClick={() => onFilterChange(option)}
                          checked={activeFilters.includes(option)}
                        />
                      </StyledFilterListCheckboxField>
                    </StyledFilterListItem>
                  ))}
                  <StyledFilterListItem>
                    <StyledFilterListButton
                      onClick={() => {
                        column.setFilterValue([]);
                        closePopover();
                      }}
                    >
                      <Link typography="body-regular">Deselect all</Link>
                    </StyledFilterListButton>
                  </StyledFilterListItem>
                </StyledFilterList>
              )}
            </FilterOptionList>
          )}
        </Popover.Content>
      )}
    </Popover>
  );
};
